<template>
  <div class="map-container">
    <div id="map" ref="mapContainer"></div>
    
    <!-- Address Search Box with Autocomplete -->
    <div class="address-search">
      <input 
        v-model="searchAddress" 
        @keyup.enter="searchLocation"
        @input="handleSearchInput"
        @focus="showSuggestions = true"
        @blur="hideSuggestions"
        type="text" 
        placeholder="Enter address or location..."
        class="search-input"
        autocomplete="off"
      />
      
      <!-- Autocomplete Suggestions -->
      <div v-if="showSuggestions && suggestions.length > 0" class="suggestions-dropdown">
        <div 
          v-for="(suggestion, index) in suggestions" 
          :key="index"
          @mousedown="selectSuggestion(suggestion)"
          class="suggestion-item"
        >
          <div class="suggestion-name">{{ suggestion.display_name }}</div>
        </div>
      </div>
      
      <!-- Loading Indicator -->
      <div v-if="searching" class="search-loading">Searching...</div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
  name: 'MapView',
  data() {
    return {
      map: null,
      markersLayer: null,
      currentData: null,
      userMarker: null,
      mapReady: false,
      openPopup: null,
      searchAddress: '',
      searching: false,
      suggestions: [],
      showSuggestions: false,
      searchTimeout: null
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    initMap() {
      if (!this.$refs.mapContainer) {
        console.error('Map container not found');
        return;
      }

      try {
        this.map = L.map(this.$refs.mapContainer, {
          zoomAnimation: false, // Disable all zoom animations
          fadeAnimation: false, // Disable fade animations
          markerZoomAnimation: false, // Disable marker animations
          zoomAnimationThreshold: 4 // Only animate for small zoom changes
        }).setView([-25.2744, 133.7751], 5);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
        }).addTo(this.map);

        this.markersLayer = L.layerGroup().addTo(this.map);
        
        // Close all popups before any zoom/pan operation
        this.map.on('zoomstart', () => {
          this.map.closePopup();
        });
        
        this.map.on('movestart', () => {
          this.map.closePopup();
        });
        
        this.mapReady = true;
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    },

    handleSearchInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // If input is too short, clear suggestions
      if (this.searchAddress.trim().length < 3) {
        this.suggestions = [];
        return;
      }

      // Debounce the autocomplete search
      this.searchTimeout = setTimeout(() => {
        this.fetchSuggestions();
      }, 300);
    },

    async fetchSuggestions() {
      if (!this.searchAddress.trim() || this.searchAddress.trim().length < 3) {
        return;
      }

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.searchAddress)}&limit=5&countrycodes=au`
        );
        
        const data = await response.json();
        
        if (data && data.length > 0) {
          this.suggestions = data;
          this.showSuggestions = true;
        } else {
          this.suggestions = [];
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        this.suggestions = [];
      }
    },

    selectSuggestion(suggestion) {
      this.searchAddress = suggestion.display_name;
      this.suggestions = [];
      this.showSuggestions = false;
      this.goToLocation(suggestion.lat, suggestion.lon, suggestion.display_name);
    },

    hideSuggestions() {
      // Delay hiding to allow click events on suggestions
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200);
    },

    async searchLocation() {
      if (!this.searchAddress.trim() || !this.map || !this.mapReady) {
        return;
      }

      // Clear suggestions
      this.suggestions = [];
      this.showSuggestions = false;

      this.searching = true;

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.searchAddress)}&limit=1&countrycodes=au`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        if (data && data.length > 0) {
          const { lat, lon, display_name } = data[0];
          this.goToLocation(lat, lon, display_name);
        } else {
          console.warn('No results found for:', this.searchAddress);
          // Try without country restriction
          await this.searchLocationGlobal();
        }
      } catch (error) {
        console.error('Geocoding error:', error);
        this.showError('Unable to search location. Please check your connection and try again.');
      } finally {
        this.searching = false;
      }
    },

    async searchLocationGlobal() {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.searchAddress)}&limit=1`
        );
        
        const data = await response.json();

        if (data && data.length > 0) {
          const { lat, lon, display_name } = data[0];
          this.goToLocation(lat, lon, display_name);
        } else {
          this.showError('Location not found. Please try a different address.');
        }
      } catch (error) {
        console.error('Global geocoding error:', error);
        this.showError('Location not found. Please try a different address.');
      }
    },

    goToLocation(lat, lon, displayName) {
      if (!this.map) return;

      try {
        this.map.flyTo([parseFloat(lat), parseFloat(lon)], 13, {
          duration: 1.5
        });

        // Add/update search marker
        if (this.userMarker) {
          this.userMarker.setLatLng([lat, lon]);
          this.userMarker.setPopupContent(displayName);
        } else {
          const searchIcon = L.divIcon({
            html: `<div style="
              width: 20px;
              height: 20px;
              background: #4299e1;
              border: 3px solid white;
              border-radius: 50%;
              box-shadow: 0 0 10px rgba(66, 153, 225, 0.8);
            "></div>`,
            className: 'search-location-marker',
            iconSize: [20, 20],
            iconAnchor: [10, 10]
          });

          this.userMarker = L.marker([lat, lon], { 
            icon: searchIcon,
            zIndexOffset: 1000
          })
            .addTo(this.map)
            .bindPopup(displayName, {
              closeButton: true,
              className: 'search-location-popup'
            });
          
          this.userMarker.openPopup();
        }

        this.$emit('location-found', { latitude: parseFloat(lat), longitude: parseFloat(lon) });
      } catch (error) {
        console.error('Error navigating to location:', error);
        this.showError('Error displaying location on map.');
      }
    },

    showError(message) {
      // Create a temporary error notification
      const notification = document.createElement('div');
      notification.className = 'search-error-notification';
      notification.textContent = message;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.classList.add('show');
      }, 10);
      
      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 3000);
    },

    loadData(geojson) {
      if (!this.map || !this.mapReady) {
        console.warn('Map not ready, deferring data load');
        setTimeout(() => this.loadData(geojson), 100);
        return;
      }

      if (!geojson || !geojson.features || geojson.features.length === 0) {
        console.warn('No valid data to display');
        return;
      }

      this.currentData = geojson;
      this.clearMarkers();
      this.addMarkers(geojson);
      this.fitBounds(geojson);
    },

    addMarkers(geojson) {
      if (!this.markersLayer) {
        console.warn('Markers layer not initialized');
        return [];
      }

      console.log('Adding markers for', geojson.features.length, 'features');
      const markers = [];

      geojson.features.forEach((feature, index) => {
        if (!feature.geometry || !feature.geometry.coordinates) {
          console.warn('Invalid feature geometry at index', index, feature);
          return;
        }

        const { coordinates } = feature.geometry;
        const { properties } = feature;

        const lat = coordinates[1];
        const lon = coordinates[0];
        
        if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          console.warn('Invalid coordinates at index', index, { lat, lon });
          return;
        }

        try {
          const icon = this.createCustomIcon(properties);

          const marker = L.marker([lat, lon], { 
            icon,
            autoPan: false,
            interactive: true,
            bubblingMouseEvents: true
          }).bindPopup(this.createPopupContent(properties, lat, lon), {
            autoPan: false,
            autoPanPadding: [50, 50],
            closeButton: true,
            autoClose: true,
            closeOnClick: false
          });

          marker.on('popupopen', () => {
            this.openPopup = marker.getPopup();
          });

          marker.on('popupclose', () => {
            if (this.openPopup === marker.getPopup()) {
              this.openPopup = null;
            }
          });

          marker.addTo(this.markersLayer);
          markers.push(marker);
        } catch (error) {
          console.error('Error creating marker at index', index, error);
        }
      });

      console.log('Successfully added', markers.length, 'markers to map');
      return markers;
    },

    createCustomIcon(properties) {
      const status = (properties.status || properties.type || '').toLowerCase();
      
      let color = '#ff6b6b';
      
      if (status.includes('vacant')) {
        color = '#ffa726';
      } else if (status.includes('derelict')) {
        color = '#d32f2f';
      } else if (status.includes('abandoned')) {
        color = '#ff6b6b';
      } else if (status.includes('ruin')) {
        color = '#8b4513';
      }

      const iconHtml = `
        <div style="
          background-color: ${color};
          width: 24px;
          height: 24px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 2px solid #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        "></div>
      `;

      return L.divIcon({
        html: iconHtml,
        className: 'custom-marker',
        iconSize: [24, 34],
        iconAnchor: [12, 34],
        popupAnchor: [0, -34]
      });
    },

    createPopupContent(properties, lat, lon) {
      const name = properties.name || 'Unnamed Location';
      const type = properties.type || 'Unknown';
      const status = properties.status || 'Unknown';
      const closureDate = properties.closureDate || properties.closure_date || '';
      const abandonmentDate = properties.abandonmentDate || properties.abandonment_date || '';

      return `
        <div style="min-width: 200px; color: #333;">
          <h3 style="margin: 0 0 10px 0; color: #4299e1; font-size: 16px;">
            ${name}
          </h3>
          <p style="margin: 5px 0; font-size: 13px;"><strong>Coordinates:</strong> ${lat.toFixed(6)}, ${lon.toFixed(6)}</p>
          <p style="margin: 5px 0; font-size: 13px;"><strong>Type:</strong> ${type}</p>
          <p style="margin: 5px 0; font-size: 13px;"><strong>Status:</strong> ${status}</p>
          ${closureDate ? `<p style="margin: 5px 0; font-size: 13px;"><strong>Closed:</strong> ${closureDate}</p>` : ''}
          ${abandonmentDate ? `<p style="margin: 5px 0; font-size: 13px;"><strong>Abandoned:</strong> ${abandonmentDate}</p>` : ''}
        </div>
      `;
    },

    clearMarkers() {
      if (this.markersLayer) {
        if (this.map) {
          this.map.closePopup();
        }
        
        this.openPopup = null;
        this.markersLayer.clearLayers();
      }
    },

    fitBounds(geojson) {
      if (!this.map || !geojson || !geojson.features || !geojson.features.length) {
        return;
      }

      const coordinates = geojson.features
        .filter(f => f.geometry && f.geometry.coordinates)
        .map(f => {
          const lat = f.geometry.coordinates[1];
          const lon = f.geometry.coordinates[0];
          return [lat, lon];
        })
        .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));

      if (coordinates.length === 0) return;

      if (coordinates.length === 1) {
        this.map.setView(coordinates[0], 15);
      } else {
        const bounds = L.latLngBounds(coordinates);
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    },

    updateFilter(filteredData) {
      if (!this.map || !this.mapReady) {
        console.warn('Map not ready for filter update');
        return;
      }

      if (this.map) {
        this.map.closePopup();
      }

      this.clearMarkers();
      if (filteredData && filteredData.features && filteredData.features.length > 0) {
        this.addMarkers(filteredData);
      }
    }
  }
};
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

#map {
  width: 100%;
  height: 100%;
  background: #0a0a0f;
}

/* Address Search Box */
.address-search {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 90vw;
  width: 500px;
}

.search-input {
  width: 100%;
  background: #000000da;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.search-input:focus {
  background: #000000da;
  border-color: rgba(66, 153, 225, 0.5);
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

/* Autocomplete Suggestions */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  background: #000000da;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.suggestion-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background: rgba(66, 153, 225, 0.2);
}

.suggestion-name {
  color: white;
  font-size: 14px;
  line-height: 1.4;
}

/* Loading Indicator */
.search-loading {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  padding: 8px 16px;
  background: #000000da;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  text-align: center;
}

/* Custom marker styling */
:deep(.custom-marker) {
  background: transparent;
  border: none;
}

:deep(.search-location-marker) {
  background: transparent;
  border: none;
}

/* Popup styling */
:deep(.leaflet-popup-content-wrapper) {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

:deep(.leaflet-popup-tip) {
  background: rgba(255, 255, 255, 0.95);
}

/* Error Notification */
:global(.search-error-notification) {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%) translateY(-20px);
  background: rgba(220, 38, 38, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 20px;
  color: white;
  font-size: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 10000;
  opacity: 0;
  transition: all 0.3s ease;
  max-width: 90vw;
  width: 400px;
  text-align: center;
}

:global(.search-error-notification.show) {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* Responsive Design */
@media (max-width: 768px) {
  .address-search {
    top: 10px;
    left: 10px;
    right: 10px;
    width: auto;
    max-width: none;
    transform: none;
  }

  .search-input {
    width: 100%;
  }

  .suggestions-dropdown {
    max-height: 200px;
  }

  :global(.search-error-notification) {
    top: 60px;
    width: calc(100% - 20px);
  }
}
</style>
